import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useNavigate, useParams } from "react-router-dom";

import APInfoText from "../../../components/APInfoText";
import APButton from "../../../components/buttons/APButton";
import APContactHelpButton from "../../../components/buttons/APContactHelpButton";
import BackButton from "../../../components/buttons/BackButton";
import HomeButton from "../../../components/buttons/HomeButton";
import InfoButton from "../../../components/buttons/InfoButton";
import PageContainer from "../../../components/PageContainer";
import RestrictedAppScope from "../../../components/RestrictedAppScope";
import { PersonaliseQuoteInputs } from "../../../helpers/quotes";
import { useFaqs } from "../../../hooks/useFaqs";
import { useQuotePersonalisation } from "../../../hooks/useQuotePersonalisation";
import { useVehicleSourcingAccess } from "../../../hooks/useVehicleSourcingAccess";
import { useAuth } from "../../../providers/AuthProvider";
import VehicleOptionTabs from "../../../quote/components/VehicleOptionTabs";

const pageTitle = "Options";

type VehicleSourcingOptionsWithQuoteProps = {
  quoteId: string;
  dossierId: string;
  opportunityId?: string;
};

const VehicleSourcingOptionsWithQuote = ({
  quoteId,
  dossierId,
  opportunityId,
}: VehicleSourcingOptionsWithQuoteProps) => {
  const navigate = useNavigate();
  const { getScopedPageUrl } = useAuth();

  const {
    control,
    handleSubmit,
    formattedQuote,
    quoteVehicleInfo,
    vehicleOptions,
    isLoading: isLoadingQuotePersonalisation,
    isSuccess: isSuccessQuotePersonalisation,
    isSubmitting,
    hasPricingRequests,
    submitOptions,
  } = useQuotePersonalisation({ quoteId, scope: "vehicle" });

  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "select-auto-ux-options-to-source",
    contextIds: {
      opportunityId: opportunityId,
    },
  });

  const onNext = async (values: PersonaliseQuoteInputs) => {
    await submitOptions(values);
    navigate(
      getScopedPageUrl("dossierVehicleSourcingManufacturerOptions", dossierId)
    );
  };

  const isLoading = isLoadingFaqs || isLoadingQuotePersonalisation;
  const isSuccess = isSuccessFaqs && isSuccessQuotePersonalisation;

  const renderAppLocation = faqProps.renderAppLocation;

  return (
    <PageContainer
      loading={isLoading}
      title={pageTitle}
      titleButton={
        isSuccess ? (
          <Box mr={1}>
            {renderAppLocation(
              "SelectAutoUXOptionsToSource-Page",
              <InfoButton />,
              true,
              true
            )}
          </Box>
        ) : undefined
      }
    >
      {isSuccess &&
        formattedQuote &&
        quoteVehicleInfo.data &&
        vehicleOptions.data && (
          <Stack spacing={2}>
            <Typography variant="labelLarge">
              Please select options for your vehicle.
            </Typography>

            <VehicleOptionTabs
              quoteVehicleInfo={quoteVehicleInfo.data}
              savingsPercentage={formattedQuote.savings.raw}
              term={formattedQuote.term.raw}
              vehicleOptions={vehicleOptions.data}
              control={control}
              flags={{
                isSubmitting,
                hasPricingRequests,
                isQuoteLocked: formattedQuote.isLocked,
                isVehicleConfigurationLocked:
                  formattedQuote.isVehicleConfigurationLocked,
                showTerms: true,
              }}
            />

            {renderAppLocation(
              "SelectAutoUXOptionsToSource-ChangeLaterText",
              <APInfoText text="Don’t worry you can change these options later." />
            )}

            <Stack
              spacing={2}
              sx={{
                display: "flex",
                flexDirection: "column",
                pt: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  columnGap: 1,
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Box
                    sx={{ display: "flex", columnGap: 1, alignItems: "center" }}
                  >
                    <BackButton />
                    <HomeButton destination="dossiersHome" />
                  </Box>
                </Box>
                <Box>
                  <APButton
                    text="Next"
                    variant="contained"
                    color="secondary"
                    onClick={handleSubmit(onNext)}
                    icon="play"
                  />
                </Box>
              </Box>
              <Box>
                <RestrictedAppScope scope="external">
                  <APContactHelpButton regardingOpportunityId={opportunityId} />
                </RestrictedAppScope>
              </Box>
            </Stack>
          </Stack>
        )}
    </PageContainer>
  );
};

const VehicleSourcingOptions = () => {
  const { dossierId } = useParams();

  if (!dossierId) {
    // TODO: improve client side error messaging
    throw new Error("dossier id param is misisng");
  }

  const {
    dossier,
    navigationCheckComplete,
    isLoading: isDossierLoading,
  } = useVehicleSourcingAccess({ dossierId });

  const primaryQuoteId = dossier.data?.opportunity?.primaryQuoteId ?? null;

  if (isDossierLoading || !navigationCheckComplete) {
    return (
      <PageContainer loading title={pageTitle}>
        {null}
      </PageContainer>
    );
  }

  if (!primaryQuoteId) {
    return (
      <PageContainer title={pageTitle} loading={false}>
        {null}
      </PageContainer>
    );
  }

  return (
    <VehicleSourcingOptionsWithQuote
      quoteId={primaryQuoteId}
      dossierId={dossierId}
      opportunityId={dossier.data?.opportunity?.id}
    />
  );
};

export default VehicleSourcingOptions;
